import { ThumbUp, ThumbUpOutlined } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

import { use, useState } from 'react';

import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import { CurrentUserContext } from '@/context/CurrentUserContext';

import { AssistantAiResponseFeedbackCommentDialog } from './buttons/AssistantAiResponseFeedbackCommentDialog';

const ASSISTANT_AI_RESPONSE_FEEDBACK_LIKE_BUTTON_ITEM_FRAGMENT = graphql(`
  fragment assistantAiResponseFeedbackLikeButtonItemFields on OrtooGptConversationItem {
    ...assistantAiResponseFeedbackCommentDialogItemFields
    _id
    response {
      responseText
    }
    feedbacksConnection {
      edges {
        node {
          ...assistantAiResponseFeedbackCommentDialogFeedbackFields
          user {
            _id
          }
          rating
          comment
        }
      }
    }
  }
`);

export const GIVE_FEEDBACK_ON_CONVERSATION_ITEM_MUTATION = graphql(`
  mutation GiveFeedbackOnConversationItem(
    $params: GiveFeedbackOnConversationItemInput
  ) {
    giveFeedbackOnConversationItem(params: $params) {
      ortooGptConversationItem {
        ...ortooGptConversationItemFields
      }
    }
  }
`);

interface Props {
  item: DocumentType<
    typeof ASSISTANT_AI_RESPONSE_FEEDBACK_LIKE_BUTTON_ITEM_FRAGMENT
  >;
}

export const AssistantAiResponseFeedbackLikeButton = ({ item }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { currentUserId } = use(CurrentUserContext);

  const { feedbacksConnection } = item ?? {};

  const feedbacks = extractConnectionProps(feedbacksConnection);

  const feedback = feedbacks?.find(
    (feedback) => feedback?.user?._id === currentUserId,
  );

  const { rating } = feedback ?? {};

  const [giveFeedbackOnConversationItem] = useMutation(
    GIVE_FEEDBACK_ON_CONVERSATION_ITEM_MUTATION,
    {
      completionMessage:
        feedback?.rating === 'POSITIVE' ? 'Liked response' : 'Unliked response',
      onCompleted(data) {
        const newFeedbacks = extractConnectionProps(
          data?.giveFeedbackOnConversationItem?.ortooGptConversationItem
            ?.feedbacksConnection,
        );

        const newFeedback = newFeedbacks?.find(
          (feedback) => feedback?.user?._id === currentUserId,
        );

        if (newFeedback?.rating === 'POSITIVE') {
          setDialogOpen(true);
        }
      },
    },
  );

  return (
    <>
      <Tooltip
        title={
          feedback?.rating === 'POSITIVE' ? 'Remove like' : 'Like this response'
        }
      >
        <IconButton
          onClick={() => {
            if (!item._id) {
              return;
            }

            giveFeedbackOnConversationItem({
              variables: {
                params: {
                  conversationItem: item._id,
                  rating:
                    feedback?.rating === 'POSITIVE' ? 'UNRATED' : 'POSITIVE',
                  comment: null,
                },
              },
            });
          }}
        >
          {rating === 'POSITIVE' ? <ThumbUp /> : <ThumbUpOutlined />}
        </IconButton>
      </Tooltip>
      <AssistantAiResponseFeedbackCommentDialog
        item={item}
        feedback={feedback}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};
